import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CrisisMonitoring = () => {
  const [alertLevel, setAlertLevel] = useState('normal');
  const [selectedRegion, setSelectedRegion] = useState('all');

  const fimiThresholds = [
    {
      id: 'threshold_001',
      name: 'Coordinated Inauthentic Behavior',
      description: 'Detection of coordinated networks spreading disinformation',
      currentLevel: 0.78,
      threshold: 0.75,
      status: 'triggered',
      region: 'Central Europe',
      lastTriggered: new Date(Date.now() - 1800000),
      affectedPlatforms: ['Facebook', 'Twitter', 'Telegram'],
      estimatedReach: 2400000,
      severity: 'high'
    },
    {
      id: 'threshold_002',
      name: 'Viral Misinformation Spread',
      description: 'Rapid propagation of false information across platforms',
      currentLevel: 0.65,
      threshold: 0.70,
      status: 'warning',
      region: 'Western Europe',
      lastTriggered: new Date(Date.now() - 7200000),
      affectedPlatforms: ['TikTok', 'Instagram', 'WhatsApp'],
      estimatedReach: 1800000,
      severity: 'medium'
    },
    {
      id: 'threshold_003',
      name: 'State-Sponsored Disinformation',
      description: 'Indicators of foreign interference in information space',
      currentLevel: 0.82,
      threshold: 0.80,
      status: 'triggered',
      region: 'Eastern Europe',
      lastTriggered: new Date(Date.now() - 3600000),
      affectedPlatforms: ['RT', 'Sputnik', 'Telegram'],
      estimatedReach: 950000,
      severity: 'critical'
    },
    {
      id: 'threshold_004',
      name: 'Election Interference Patterns',
      description: 'Attempts to manipulate electoral processes through disinformation',
      currentLevel: 0.45,
      threshold: 0.60,
      status: 'monitoring',
      region: 'Northern Europe',
      lastTriggered: null,
      affectedPlatforms: ['Facebook', 'Twitter', 'YouTube'],
      estimatedReach: 0,
      severity: 'low'
    }
  ];

  const escalationProtocols = [
    {
      id: 'protocol_001',
      name: 'FIMI Level 1 Response',
      description: 'Standard response for coordinated disinformation campaigns',
      triggerConditions: ['Coordinated behavior detected', 'Cross-platform spread', 'Reach > 1M'],
      actions: [
        'Notify EDMO hubs',
        'Initiate fact-checking workflow',
        'Alert platform partners',
        'Begin evidence collection'
      ],
      responsibleTeams: ['EFCSN Network', 'Platform Liaisons'],
      timeframe: '2 hours',
      status: 'active'
    },
    {
      id: 'protocol_002',
      name: 'FIMI Level 2 Response',
      description: 'Enhanced response for high-impact disinformation threats',
      triggerConditions: ['State-sponsored indicators', 'Critical infrastructure targeting', 'Election period'],
      actions: [
        'Escalate to national authorities',
        'Coordinate with EU institutions',
        'Activate crisis communication',
        'Deploy rapid response team'
      ],
      responsibleTeams: ['Crisis Response Center', 'Government Liaisons'],
      timeframe: '1 hour',
      status: 'standby'
    },
    {
      id: 'protocol_003',
      name: 'Democracy Shield Protocol',
      description: 'Emergency response for threats to democratic processes',
      triggerConditions: ['Election interference confirmed', 'Widespread manipulation', 'National security implications'],
      actions: [
        'Emergency funding activation',
        'Multi-agency coordination',
        'Public warning systems',
        'Legal action initiation'
      ],
      responsibleTeams: ['Democracy Shield Task Force', 'Legal Affairs'],
      timeframe: '30 minutes',
      status: 'standby'
    }
  ];

  const recentAlerts = [
    {
      id: 'alert_001',
      title: 'Coordinated Climate Disinformation Campaign Detected',
      description: 'Network of 847 accounts spreading false claims about EU Green Deal policies',
      severity: 'high',
      timestamp: new Date(Date.now() - 1800000),
      region: 'Central Europe',
      status: 'active',
      assignedTo: 'Crisis Response Team Alpha',
      platforms: ['Facebook', 'Twitter'],
      estimatedReach: 2400000,
      actionsTaken: ['Platform notification sent', 'Fact-checking initiated', 'EDMO hubs alerted']
    },
    {
      id: 'alert_002',
      title: 'State-Sponsored Media Amplification',
      description: 'Unusual amplification patterns detected for RT and Sputnik content',
      severity: 'critical',
      timestamp: new Date(Date.now() - 3600000),
      region: 'Eastern Europe',
      status: 'escalated',
      assignedTo: 'Government Liaison Team',
      platforms: ['Telegram', 'VK', 'Odnoklassniki'],
      estimatedReach: 950000,
      actionsTaken: ['National authorities notified', 'EU Commission briefed', 'Legal review initiated']
    },
    {
      id: 'alert_003',
      title: 'Deepfake Video Viral Spread',
      description: 'AI-generated video of EU official making false statements gaining traction',
      severity: 'high',
      timestamp: new Date(Date.now() - 5400000),
      region: 'Multi-regional',
      status: 'resolved',
      assignedTo: 'Technical Analysis Team',
      platforms: ['TikTok', 'YouTube', 'Twitter'],
      estimatedReach: 1200000,
      actionsTaken: ['Content removed', 'Technical analysis published', 'Media advisory issued']
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'triggered': return 'text-red-600 bg-red-50 border-red-200';
      case 'warning': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'monitoring': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'normal': return 'text-green-600 bg-green-50 border-green-200';
      case 'active': return 'text-blue-600 bg-blue-50';
      case 'escalated': return 'text-red-600 bg-red-50';
      case 'resolved': return 'text-green-600 bg-green-50';
      case 'standby': return 'text-gray-600 bg-gray-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-red-600';
      case 'high': return 'text-orange-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const formatTimeAgo = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInMinutes < 1440) {
      return `${Math.floor(diffInMinutes / 60)}h ago`;
    } else {
      return `${Math.floor(diffInMinutes / 1440)}d ago`;
    }
  };

  const triggeredThresholds = fimiThresholds?.filter(t => t?.status === 'triggered')?.length;
  const warningThresholds = fimiThresholds?.filter(t => t?.status === 'warning')?.length;

  return (
    <div className="bg-card rounded-lg border border-border shadow-soft">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-red-500/10 rounded-lg flex items-center justify-center">
              <Icon name="Shield" size={20} className="text-red-500" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Crisis Monitoring</h3>
              <p className="text-sm text-muted-foreground">FIMI threshold monitoring and escalation protocols</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className={`px-3 py-2 rounded-lg border ${
              triggeredThresholds > 0 ? 'bg-red-50 border-red-200 text-red-600' :
              warningThresholds > 0 ? 'bg-orange-50 border-orange-200 text-orange-600': 'bg-green-50 border-green-200 text-green-600'
            }`}>
              <div className="flex items-center space-x-2">
                <Icon name={
                  triggeredThresholds > 0 ? 'AlertTriangle' :
                  warningThresholds > 0 ? 'AlertCircle' : 'CheckCircle'
                } size={16} />
                <span className="text-sm font-medium">
                  {triggeredThresholds > 0 ? 'ALERT' :
                   warningThresholds > 0 ? 'WARNING' : 'NORMAL'}
                </span>
              </div>
            </div>
            <Button variant="outline" size="sm" iconName="Settings">
              Configure
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-3 bg-red-50 rounded-lg border border-red-200">
            <div className="flex items-center space-x-2">
              <Icon name="AlertTriangle" size={16} className="text-red-600" />
              <span className="text-sm font-medium text-red-600">Triggered Thresholds</span>
            </div>
            <div className="text-2xl font-bold text-red-600 mt-1">{triggeredThresholds}</div>
          </div>
          <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
            <div className="flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} className="text-orange-600" />
              <span className="text-sm font-medium text-orange-600">Warning Level</span>
            </div>
            <div className="text-2xl font-bold text-orange-600 mt-1">{warningThresholds}</div>
          </div>
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center space-x-2">
              <Icon name="Eye" size={16} className="text-blue-600" />
              <span className="text-sm font-medium text-blue-600">Active Monitoring</span>
            </div>
            <div className="text-2xl font-bold text-blue-600 mt-1">{fimiThresholds?.length - triggeredThresholds - warningThresholds}</div>
          </div>
        </div>
      </div>
      <div className="p-6">
        <div className="space-y-6">
          {/* FIMI Thresholds */}
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-3">FIMI Threshold Status</h4>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {fimiThresholds?.map((threshold) => (
                <div
                  key={threshold?.id}
                  className="p-4 bg-muted/30 rounded-lg border border-border hover:bg-muted/50 transition-smooth"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h5 className="text-sm font-semibold text-foreground">{threshold?.name}</h5>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(threshold?.status)}`}>
                          {threshold?.status?.toUpperCase()}
                        </span>
                        <Icon name="AlertTriangle" size={14} className={getSeverityColor(threshold?.severity)} />
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{threshold?.description}</p>
                      <div className="text-xs text-muted-foreground">
                        Region: {threshold?.region} • Last triggered: {formatTimeAgo(threshold?.lastTriggered)}
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" iconName="MoreHorizontal" />
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-3">
                    <div>
                      <div className="text-xs text-muted-foreground">Current Level</div>
                      <div className="flex items-center space-x-2">
                        <div className="flex-1 bg-muted rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all ${
                              threshold?.currentLevel >= threshold?.threshold ? 'bg-red-500' : 'bg-blue-500'
                            }`}
                            style={{ width: `${threshold?.currentLevel * 100}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium text-foreground">
                          {(threshold?.currentLevel * 100)?.toFixed(0)}%
                        </span>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Threshold</div>
                      <div className="text-sm font-medium text-foreground">{(threshold?.threshold * 100)?.toFixed(0)}%</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Estimated Reach</div>
                      <div className="text-sm font-medium text-foreground">
                        {threshold?.estimatedReach > 0 ? `${(threshold?.estimatedReach / 1000000)?.toFixed(1)}M` : 'N/A'}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex flex-wrap gap-1">
                      {threshold?.affectedPlatforms?.slice(0, 3)?.map((platform, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 text-xs bg-secondary/10 text-secondary rounded-full"
                        >
                          {platform}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button variant="outline" size="sm">
                        Details
                      </Button>
                      {threshold?.status === 'triggered' && (
                        <Button variant="default" size="sm">
                          Escalate
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Alerts */}
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-3">Recent Crisis Alerts</h4>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {recentAlerts?.map((alert) => (
                <div
                  key={alert?.id}
                  className="p-4 bg-muted/30 rounded-lg border border-border hover:bg-muted/50 transition-smooth cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h5 className="text-sm font-semibold text-foreground">{alert?.title}</h5>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(alert?.status)}`}>
                          {alert?.status?.toUpperCase()}
                        </span>
                        <Icon name="AlertTriangle" size={14} className={getSeverityColor(alert?.severity)} />
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{alert?.description}</p>
                      <div className="text-xs text-muted-foreground">
                        {formatTimeAgo(alert?.timestamp)} • {alert?.region} • Assigned to: {alert?.assignedTo}
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" iconName="ExternalLink" />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="text-xs text-muted-foreground">
                      Reach: {(alert?.estimatedReach / 1000000)?.toFixed(1)}M • 
                      Platforms: {alert?.platforms?.join(', ')}
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button variant="outline" size="sm">
                        View Report
                      </Button>
                      {alert?.status === 'active' && (
                        <Button variant="default" size="sm">
                          Take Action
                        </Button>
                      )}
                    </div>
                  </div>

                  {alert?.actionsTaken?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-border">
                      <div className="text-xs text-muted-foreground mb-1">Actions Taken:</div>
                      <div className="flex flex-wrap gap-1">
                        {alert?.actionsTaken?.map((action, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 text-xs bg-green-50 text-green-600 rounded-full"
                          >
                            ✓ {action}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrisisMonitoring;